import { useState, useCallback, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { LABEL, CUSTOM_MESSAGES } from '@veriables';
import { voidCheck as voidCheckAction } from '@reducers/orders';
import { updateChecksAfterResponse } from '@reducers/storeChecks';
import { login } from '@reducers/oauth';
import { flowType } from '@utility/userInfo';
import { APP_CONFIG } from '@constants';
import Modal from '@components/common/Modal';
import ModalHeader from '@components/common/ModalHeader';
import { TouchableButton } from '@components/common/Touchable';
import Loader from '@components/common/Loader/newLoaderModal';

const FullWidthModal = lazy(() => import('@components/common/Modal/FullWidthModal'));
const LoginKeyboard = lazy(() => import('@components/common/formElements/SimpleKeyboard/loginKeyboard'));

const useVoidCheck = ({ checkData, authInfo, deviceInfo, userInfo, serverAppTheme, setErrorPopUp, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isManagerCheckOpen, setIsManagerCheckOpen] = useState(false);
  const [isVoidOpen, setIsVoidOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const open = useCallback(() => {
    if (userInfo && userInfo.jobTypeId !== 1 && authInfo?.nonIntegratedPos) {
      setIsManagerCheckOpen(true);
    } else {
      setIsVoidOpen(true);
    }
  }, [userInfo, authInfo?.nonIntegratedPos]);

  const validateManagerCode = useCallback(data => {
    return new Promise((resolve, reject) => {
      if (!data.managerCode) {
        setErrorPopUp({ res: CUSTOM_MESSAGES().MANAGER_CODE_IS_REQUIRED, noErrorTxt: true });
        reject(new Error(''));
      } else {
        resolve();
      }
    });
  }, [setErrorPopUp]);

  const handleManagerSubmit = useCallback((data, empCard) => {
    const dataToSend = {
      latitude: '92',
      longitude: '92',
      manager: true,
      deviceId: deviceInfo?.deviceId,
      employeeCode: data.managerCode,
      employeeCard: empCard || null,
    };
    dispatch(login(dataToSend, true))
      .then(() => {
        setIsManagerCheckOpen(false);
        setIsVoidOpen(true);
      })
      .catch(err => setErrorPopUp({ res: err.message }));
  }, [dispatch, deviceInfo, setErrorPopUp]);

  const handleConfirm = useCallback(() => {
    const payload = {
      employeeId: Number(userInfo?.id),
      storeId: deviceInfo?.storeId,
      checkHeaderId: checkData?.id,
      employeeCard: userInfo?.employeeCard,
      deviceId: deviceInfo?.deviceId,
    };
    setIsVoidOpen(false);
    setIsLoading(true);
    dispatch(voidCheckAction(payload))
      .then(res => {
        dispatch(updateChecksAfterResponse(res));
        setIsLoading(false);
        onClose();
        navigate(`${APP_CONFIG.BASE_URL}/${flowType()}/checks`, { replace: true });
      })
      .catch(err => {
        setIsLoading(false);
        setErrorPopUp({ res: err.message });
      });
  }, [dispatch, checkData, deviceInfo, userInfo, onClose, setErrorPopUp, navigate]);

  const voidStyle = {
    backgroundColor: serverAppTheme?.popUp_bg,
    border: serverAppTheme?.popupBorder,
    boxShadow: serverAppTheme?.popUp_box_shadow,
    borderRadius: '16px',
    width: '486px',
    margin: 'auto',
    minHeight: checkData?.paidAmount > 0 ? '235px' : '216px',
  };

  const jsx = (
    <>
      {isLoading && <Loader />}
      <Suspense fallback={null}>
        {isManagerCheckOpen && (
          <FullWidthModal
            isOpen={isManagerCheckOpen}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              pointerEvents: 'auto',
              backgroundColor: serverAppTheme?.popUp_bg,
              backgroundClip: 'padding-box',
              height: '100%',
            }}
            customClass="h-100 w-100"
            customClass2="h-100 w-100"
            noBackground
          >
            <div
              className="d-flex flex-column align-items-center text-center position-relative h-100"
              style={{ color: serverAppTheme?.primary_text }}
            >
              <ModalHeader tileHeader={LABEL().VOID_CHECK} />
              <div className="py-2 w-100 d-flex flex-column justify-content-center align-items-center">
                <div className="mt-2 h-10 fw-500 fs_20">{CUSTOM_MESSAGES().ENTER_MANAGER_CODE}</div>
                <LoginKeyboard
                  containerClass="mt-3 w-60 h-78 px-2"
                  customClass="py-2"
                  type="number"
                  name="managerCode"
                  validate={validateManagerCode}
                  buttonName={flowType() === 'kiosk' ? LABEL().KIOSK_SUBMIT : LABEL().SUBMIT}
                  placeholder={LABEL().ENTER_OTP}
                  onSubmit={handleManagerSubmit}
                  auth={authInfo}
                  serverAppTheme={serverAppTheme}
                  closeBtn={LABEL().CANCEL}
                  handleClose={() => setIsManagerCheckOpen(false)}
                />
              </div>
            </div>
          </FullWidthModal>
        )}
      </Suspense>
      <Modal
        isOpen={isVoidOpen}
        isModalCenter
        noBackground
        style={voidStyle}
        customClass="h-100 w-100"
        customClass2="h-100 w-100"
      >
        <div
          className="m-2 justify-content-center align-items-center text-center h-100"
          style={{ color: serverAppTheme?.primary_text }}
        >
          <div className="col-12 py-2 fs_18 fw-700 text-center h-25">Are You Sure?</div>
          <div className="col-12 d-flex p-0 py-1 h-auto justify-content-center">
            <div className="col-9 px-3 fs_15 fw-normal text-center">
              {checkData?.paidAmount > 0 ? (
                <>
                  <text className="fs_15">{CUSTOM_MESSAGES().CHECK_VOID_ERRER}</text>
                  <text> {CUSTOM_MESSAGES().CHECK_VOID} </text>
                </>
              ) : (
                CUSTOM_MESSAGES().CHECK_VOID
              )}
            </div>
          </div>
          <div
            className="col-11 d-flex mt-2 justify-content-center align-items-center align-items-end text-center h-20 position-absolute"
            style={{ bottom: '26px', left: '20px' }}
          >
            <TouchableButton
              className={cn('col-6 h-100 mr-1 btn90 fs_24')}
              style={{
                background: serverAppTheme?.OFF_Premise_Btn,
                border: serverAppTheme?.cancleBtn_Border,
                color: serverAppTheme?.cancleBtn_font,
              }}
              type="button"
              onClick={() => setIsVoidOpen(false)}
              rippleEffect
            >
              {LABEL().NO}
            </TouchableButton>
            <TouchableButton
              className={cn('col-6 h-100 ml-1 btn90 btn-ripple fs_24')}
              style={{
                background: serverAppTheme?.secondaryBtn,
                color: serverAppTheme?.btn_font_color,
              }}
              type="button"
              onClick={handleConfirm}
              rippleEffect
            >
              {LABEL().YES}
            </TouchableButton>
          </div>
        </div>
      </Modal>
    </>
  );

  return { open, jsx };
};

export default useVoidCheck;
